/**
 * Catalog of UI access actions for study-level configuration (editor only until wired in app code).
 * defaultRoles mirror primary org roles (admin, support, study, site). Read-only and blinded access
 * are user-level flags (user.readOnly, user.blinded), not rows in this matrix — the app combines
 * those flags with hasRole where needed (e.g. sidebar, /eventReport layout).
 */

/** All role codes that may appear in the Access tab checkboxes. */
export const ACCESS_ROLE_OPTIONS = [
  'admin',
  'support',
  'study',
  'site',
];

export const ACCESS_UI_GROUPS = [
  {
    id: 'diaryForms',
    title: 'Diary forms',
    description: 'Diary Entries (/studyForms), form definitions, and related tools.',
  },
  {
    id: 'participants',
    title: 'Participants',
    description: 'Participant list pages, participant details.',
  },
  {
    id: 'sites',
    title: 'Sites',
    description: 'Site list and site detail panels.',
  },
  {
    id: 'studies',
    title: 'Studies',
    description: 'Study list and study-level configuration UI.',
  },
  {
    id: 'schedule',
    title: 'Participant schedule',
    description: '/schedule — subject activity timeline and measurement tools.',
  },
  {
    id: 'studySchedules',
    title: 'Study schedules',
    description: 'Draft/published study schedules and activities.',
  },
  {
    id: 'adminDataTools',
    title: 'Admin data tools',
    description: 'Devices, languages, localization, lookup management, and other admin tools.',
  },
  {
    id: 'auditLogs',
    title: 'Audit logs',
    description: 'Viewing audit log modals where the UI checks roles.',
  },
  {
    id: 'navigation',
    title: 'Navigation (sidebar)',
    description:
      'Role gates on sidebar menus and footer changelog link. Database switcher and Tetris easter egg are always admin/support only (not configurable here).',
  },
  {
    id: 'shell',
    title: 'Header',
    description: 'Top navigation menu items.',
  },
];

/**
 * @typedef {Object} AccessUiAction
 * @property {string} key - Stable id stored in permissions.actionRoles[key]
 * @property {string} groupId
 * @property {string} label
 * @property {string} [description]
 * @property {string[]} defaultRoles
 * @property {boolean} [requiresWrite] - When true, matches patterns that also require !isReadOnly()
 */

/** @type {AccessUiAction[]} */
export const ACCESS_UI_ACTIONS = [
  // --- Diary and subject forms ---
  {
    key: 'studyForms.add',
    groupId: 'diaryForms',
    label: 'Add diary entries',
    description: '/studyForms — add subject form (also requires Diary Editing feature when enforced).',
    defaultRoles: ['admin', 'support', 'study', 'site'],
    requiresWrite: true,
  },
  {
    key: 'studyForms.edit',
    groupId: 'diaryForms',
    label: 'Edit diary entries',
    description: '/studyForms — edit existing entry (also requires Diary Editing when enforced).',
    defaultRoles: ['admin', 'support', 'study', 'site'],
    requiresWrite: true,
  },
  {
    key: 'studyForms.delete',
    groupId: 'diaryForms',
    label: 'Delete diary entries',
    description: '/studyForms — delete from row menu.',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'studyForms.viewDebugJson',
    groupId: 'diaryForms',
    label: 'View diary debug JSON',
    description: '/studyForms — debug payload from row menu.',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'forms.definitionAdminTools',
    groupId: 'diaryForms',
    label: 'Form definitions — admin tools',
    description: '/forms — sections gated by admin role (e.g. advanced definition actions).',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },

  // --- Participants ---
  {
    key: 'participants.persons.add',
    groupId: 'participants',
    label: 'Add participants (/persons)',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.persons.edit',
    groupId: 'participants',
    label: 'Edit participants (/persons)',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.persons.delete',
    groupId: 'participants',
    label: 'Delete participants (/persons)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'participants.persons.toggleEligibility',
    groupId: 'participants',
    label: 'Toggle upgrade eligibility (/persons)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'participants.persons.assignSite',
    groupId: 'participants',
    label: 'Assign participant site (/persons)',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'participants.allPersons.add',
    groupId: 'participants',
    label: 'Add participants (All participants)',
    defaultRoles: ['admin', 'support', 'study', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.allPersons.edit',
    groupId: 'participants',
    label: 'Edit participants (All participants)',
    defaultRoles: ['admin', 'support', 'study', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.allPersons.delete',
    groupId: 'participants',
    label: 'Delete participants (All participants)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'participants.allPersons.toggleEligibility',
    groupId: 'participants',
    label: 'Toggle upgrade eligibility (All participants)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'participants.details.edit',
    groupId: 'participants',
    label: 'Edit participant (details panel)',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.details.viewAuditLogs',
    groupId: 'participants',
    label: 'View audit logs (participant details)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'participants.details.viewPin',
    groupId: 'participants',
    label: 'View PIN (participant details)',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.details.viewSubjectId',
    groupId: 'participants',
    label: 'View subject identifier (participant details)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'participants.details.manageFormProxies',
    groupId: 'participants',
    label: 'Manage form proxies (participant details)',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'participants.details.toggleEligibility',
    groupId: 'participants',
    label: 'Toggle eligibility from participant details',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'participants.personForm.adminCapabilities',
    groupId: 'participants',
    label: 'Person form — admin-only branches',
    description: 'New/edit person modal — paths that require admin (or org admin).',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },

  // --- Sites ---
  {
    key: 'sites.add',
    groupId: 'sites',
    label: 'Add sites',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'sites.edit',
    groupId: 'sites',
    label: 'Edit sites',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'sites.delete',
    groupId: 'sites',
    label: 'Delete sites',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'sites.details.edit',
    groupId: 'sites',
    label: 'Edit site (site details panel)',
    defaultRoles: ['admin', 'support', 'study'],
    requiresWrite: true,
  },
  {
    key: 'sites.details.viewAuditLogs',
    groupId: 'sites',
    label: 'View audit logs (site details panel)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },

  // --- Studies ---
  {
    key: 'studies.add',
    groupId: 'studies',
    label: 'Add studies',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'studies.edit',
    groupId: 'studies',
    label: 'Edit studies',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'studies.configure',
    groupId: 'studies',
    label: 'Configure study (open study settings)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'studies.delete',
    groupId: 'studies',
    label: 'Delete studies',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'studies.studySettings.adminActions',
    groupId: 'studies',
    label: 'Study settings — admin-only actions',
    description: 'Export/import configuration, etc.',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },

  // --- Participant schedule ---
  {
    key: 'schedule.viewAuditLogs',
    groupId: 'schedule',
    label: 'View audit logs (/schedule)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'schedule.startStopPhases',
    groupId: 'schedule',
    label: 'Start/stop schedule phases',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'schedule.editPhases',
    groupId: 'schedule',
    label: 'Edit schedule phases',
    defaultRoles: ['admin', 'support', 'site'],
    requiresWrite: true,
  },
  {
    key: 'schedule.completeMeasurements',
    groupId: 'schedule',
    label: 'Complete measurement activities',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'schedule.addMeasurementActivity',
    groupId: 'schedule',
    label: 'Add measurement activity (toolbar)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'schedule.measurementAdminTools',
    groupId: 'schedule',
    label: 'Measurement row — admin tools',
    description: 'Start measurement, import JSON, etc. on MEAS rows.',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'schedule.subjectActivityEditor.admin',
    groupId: 'schedule',
    label: 'Subject activity editor — admin',
    description: 'SubjectActivityEditor — controls tied to admin user role.',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },

  // --- Study schedules ---
  {
    key: 'studySchedules.viewAuditLogs',
    groupId: 'studySchedules',
    label: 'View audit logs (study schedules)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: false,
  },
  {
    key: 'studySchedules.activityForm.admin',
    groupId: 'studySchedules',
    label: 'Activity form — admin-only controls',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },

  // --- Admin data tools ---
  {
    key: 'devices.add',
    groupId: 'adminDataTools',
    label: 'Add devices',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'devices.edit',
    groupId: 'adminDataTools',
    label: 'Edit devices',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'devices.delete',
    groupId: 'adminDataTools',
    label: 'Delete devices',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'phoneInventory.add',
    groupId: 'adminDataTools',
    label: 'Add participant phone inventory',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'phoneInventory.edit',
    groupId: 'adminDataTools',
    label: 'Edit participant phone inventory',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'phoneInventory.delete',
    groupId: 'adminDataTools',
    label: 'Delete participant phone inventory',
    defaultRoles: ['admin'],
    requiresWrite: true,
  },
  {
    key: 'languages.add',
    groupId: 'adminDataTools',
    label: 'Add languages',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'languages.edit',
    groupId: 'adminDataTools',
    label: 'Edit languages',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'languages.delete',
    groupId: 'adminDataTools',
    label: 'Delete languages',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'localization.add',
    groupId: 'adminDataTools',
    label: 'Add localization entries',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'localization.edit',
    groupId: 'adminDataTools',
    label: 'Edit localization entries',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'localization.delete',
    groupId: 'adminDataTools',
    label: 'Delete localization entries',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'lookup.addGroups',
    groupId: 'adminDataTools',
    label: 'Lookup — add groups',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'lookup.editGroups',
    groupId: 'adminDataTools',
    label: 'Lookup — edit groups',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'lookup.deleteGroups',
    groupId: 'adminDataTools',
    label: 'Lookup — delete groups',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'lookup.addTypes',
    groupId: 'adminDataTools',
    label: 'Lookup — add types',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'lookup.editTypes',
    groupId: 'adminDataTools',
    label: 'Lookup — edit types',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'lookup.deleteTypes',
    groupId: 'adminDataTools',
    label: 'Lookup — delete types',
    defaultRoles: ['admin', 'support'],
    requiresWrite: true,
  },
  {
    key: 'subjectZeros.exportGlucoseJson',
    groupId: 'adminDataTools',
    label: 'CGM activities — export glucose JSON',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'subjectZeros.mergeActivities',
    groupId: 'adminDataTools',
    label: 'CGM activities — merge activities',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'subjectZeros.timeTravelSensor',
    groupId: 'adminDataTools',
    label: 'CGM activities — time travel sensor',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'subjectZeros.fixZeroActivityGuid',
    groupId: 'adminDataTools',
    label: 'CGM activities — fix zero activity GUID',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'subjectZeros.deleteActivities',
    groupId: 'adminDataTools',
    label: 'CGM activities — delete activities',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },

  // --- Audit logs (pages using hasAuditLogAccess) ---
  {
    key: 'auditLogs.view',
    groupId: 'auditLogs',
    label: 'View audit logs (standard pages)',
    description: 'Matches hasAuditLogAccess — admin and support.',
    defaultRoles: ['admin', 'support'],
    requiresWrite: false,
  },

  // --- Sidebar ---
  {
    key: 'sidebar.menu.eventReport',
    groupId: 'navigation',
    label: 'Menu — Event report',
    description: 'Admin or support by role; any user with user.readOnly also (matches Sidebar + layout).',
    defaultRoles: ['admin', 'support'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.participantReports',
    groupId: 'navigation',
    label: 'Menu — Participant reports (submenu parent)',
    description: 'Shown when not blinded; clinical roles or read-only user with user.readOnly.',
    defaultRoles: ['admin', 'study', 'support', 'site'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.supportTools',
    groupId: 'navigation',
    label: 'Menu — Support tools (log entries, on-demand, devices, CGM activities)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.generalReportsShell',
    groupId: 'navigation',
    label: 'Menu — General reports (outer section)',
    description: 'Clinical roles or user.readOnly.',
    defaultRoles: ['admin', 'support', 'study', 'site'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.generalReports.blindedDataItems',
    groupId: 'navigation',
    label:
      'Menu — General reports items (CGM readings, SMBG, diary entry details, insulin totals)',
    description: 'Hidden when user.blinded; otherwise clinical roles or user.readOnly.',
    defaultRoles: ['admin', 'study', 'support', 'site'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.generalReports.subjectCgm',
    groupId: 'navigation',
    label: 'Menu — Subject CGM (study reports)',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.generalReports.sensorCgm',
    groupId: 'navigation',
    label: 'Menu — Sensor CGM (study reports)',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.menu.generalReports.studyAnalytics',
    groupId: 'navigation',
    label: 'Menu — Study analytics (All participants, period reports, …)',
    defaultRoles: ['admin', 'support', 'study'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.visibility.subjectOrSensorCgmStats',
    groupId: 'navigation',
    label: 'Sidebar — show Subject/Sensor CGM stats entries',
    description: 'Reactive visibility for CGM stats menu rows.',
    defaultRoles: ['admin', 'support', 'study'],
    requiresWrite: false,
  },
  {
    key: 'sidebar.footer.changelogLink',
    groupId: 'navigation',
    label: 'Footer — changelog link (build number)',
    defaultRoles: ['admin', 'support'],
    requiresWrite: false,
  },

  // --- Shell ---
  {
    key: 'shell.header.adminMenus',
    groupId: 'shell',
    label: 'Header — admin menu (gear)',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'shell.header.documentationMenu',
    groupId: 'shell',
    label: 'Header — documentation menu (book)',
    description: 'Dropdown to the left of the gear menu; entries (e.g. API docs) can each have their own keys.',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
  {
    key: 'apidocs.access',
    groupId: 'shell',
    label: 'API documentation page',
    description: 'Also used for the “API documentation” item in the header documentation menu.',
    defaultRoles: ['admin'],
    requiresWrite: false,
  },
];

const policyByKey = Object.fromEntries(
  ACCESS_UI_ACTIONS.map((a) => [
    a.key,
    { defaultRoles: [...a.defaultRoles], requiresWrite: a.requiresWrite === true },
  ])
);

export function getAccessActionPolicy(actionKey) {
  return policyByKey[actionKey] || null;
}

export function defaultRolesForAccessAction(actionKey) {
  const p = policyByKey[actionKey];
  return p ? [...p.defaultRoles] : [];
}

export function accessActionRequiresWrite(actionKey) {
  return policyByKey[actionKey]?.requiresWrite === true;
}

export function actionsForAccessGroup(groupId) {
  return ACCESS_UI_ACTIONS.filter((a) => a.groupId === groupId);
}
